import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, LoadingRows } from '../../components/states';
import { Panel } from '../../components/ui';
import { useNotify } from '../../lib/notify';
import { timelineKey } from './ActivityTimeline';
import type { TenantDetail } from './api';

type ShopMenu = Schemas['TenantMenus']['menus'][number];

const menusKey = (tenantId: string) => ['platform', 'tenants', 'menus', tenantId] as const;

/** Menus in their sidebar sections, keeping the sidebar order. */
function bySection(menus: ShopMenu[]): [string, ShopMenu[]][] {
  const out = new Map<string, ShopMenu[]>();
  for (const m of menus) out.set(m.group, [...(out.get(m.group) ?? []), m]);
  return [...out];
}

/**
 * Tenant → Menus: one switch per menu of the shop's left sidebar (web and desktop), all on by
 * default. A menu turned off disappears for everyone in that shop, and its pages say it's off.
 */
export function MenusTab({ tenant }: { tenant: Pick<TenantDetail, 'id' | 'name'> }) {
  const { can } = useAuth();
  const manage = can('tenants.menus');
  const qc = useQueryClient();
  const notify = useNotify();
  const q = useQuery({
    queryKey: menusKey(tenant.id),
    queryFn: () =>
      unwrap(api.GET('/platform/tenants/{id}/menus', { params: { path: { id: tenant.id } } })),
  });
  // Off menus as edited here; null until the first change.
  const [edited, setEdited] = useState<Set<ShopMenu['key']> | null>(null);
  const save = useMutation({
    mutationFn: (hiddenMenus: ShopMenu['key'][]) =>
      unwrap(
        api.PUT('/platform/tenants/{id}/menus', {
          params: { path: { id: tenant.id } },
          body: { hiddenMenus },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(menusKey(tenant.id), data);
      void qc.invalidateQueries({ queryKey: timelineKey(tenant.id) });
      setEdited(null);
      notify.success('Menus saved');
    },
    onError: (e) => notify.error(e),
  });

  if (q.isPending) return <LoadingRows rows={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const menus = q.data.menus;
  const saved = new Set(menus.filter((m) => !m.visible).map((m) => m.key));
  const hidden = edited ?? saved;
  const dirty = hidden.size !== saved.size || [...hidden].some((k) => !saved.has(k));

  const toggle = (key: ShopMenu['key'], on: boolean) => {
    const next = new Set(hidden);
    if (on) next.delete(key);
    else next.add(key);
    setEdited(next);
  };

  return (
    <Panel
      title="Menus"
      subtitle={`Turn off a menu to hide it from ${tenant.name}’s sidebar on web and desktop. Staff permissions still apply to what stays on.`}
      action={
        manage ? (
          <Stack direction="row" spacing={1}>
            <Button
              disabled={hidden.size === 0 || save.isPending}
              onClick={() => setEdited(new Set())}
            >
              Turn all on
            </Button>
            <Button
              variant="contained"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(menus.filter((m) => hidden.has(m.key)).map((m) => m.key))}
            >
              Save
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Stack spacing={2.5}>
        {bySection(menus).map(([group, items]) => (
          <Box key={group}>
            <Typography variant="overline" color="text.secondary">
              {group}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                columnGap: 2,
              }}
            >
              {items.map((m) => (
                <FormControlLabel
                  key={m.key}
                  label={m.label}
                  control={
                    <Switch
                      checked={!hidden.has(m.key)}
                      disabled={!manage || save.isPending}
                      onChange={(_, on) => toggle(m.key, on)}
                    />
                  }
                />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Panel>
  );
}
